import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { Task, TaskSection, TaskTag } from '../interfaces/Task';
import { useTranslation } from 'react-i18next';
import FolderIcon from './FolderIcon';
import TaskIcon from './TaskIcon';

interface SelectedItem {
    id: string;
    type: 'section' | 'task';
}

interface FolderContentProps {
    currentFolder: TaskSection | null;
    folderPath: TaskSection[];
    subfolders: TaskSection[];
    tasks: Task[];
    viewMode: 'icons' | 'list';
    onViewModeChange: (mode: 'icons' | 'list') => void;
    onNavigateToFolder: (folderId: string | null) => void;
    onEditTask: (taskId: string) => void;
    onDeleteTask: (taskId: string) => void;
    onDuplicateTask: (task: Task) => void;
    onShareTask?: (task: Task) => void;
    onShareLinkTask?: (task: Task) => void;
    onCreateFolder: () => void;
    onCreateSubfolder: (parentId: string) => void;
    onCreateTask: () => void;
    onEditFolder: (section: TaskSection) => void;
    onDeleteFolder: (sectionId: string) => void;
    onDropItem: (itemId: string, itemType: 'section' | 'task', targetFolderId: string | null) => void;
    // Filters
    selectedTag: string;
    onTagChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    selectedProficiencyLevel: string;
    onProficiencyLevelChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    selectedAim: string;
    onAimChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onClearFilters?: () => void;
    tags: TaskTag[];
    proficiencyLevels: { value: number; description: string }[];
    aims: { value: number; description: string }[];
    // Selection
    selectedItems?: SelectedItem[];
    onItemSelect?: (id: string, type: 'section' | 'task', ctrlKey: boolean) => void;
    onClearSelection?: () => void;
    onDeleteSelected?: () => void;
    isItemSelected?: (id: string, type: 'section' | 'task') => boolean;
}

export default function FolderContent(props: FolderContentProps) {
    const { t } = useTranslation();
    const {
        currentFolder,
        folderPath,
        subfolders,
        tasks,
        viewMode,
        onViewModeChange,
        onNavigateToFolder,
        onEditTask,
        onDeleteTask,
        onDuplicateTask,
        onShareTask,
        onShareLinkTask,
        onCreateFolder,
        onCreateSubfolder,
        onCreateTask,
        onEditFolder,
        onDeleteFolder,
        onDropItem,
        selectedTag,
        onTagChange,
        selectedProficiencyLevel,
        onProficiencyLevelChange,
        selectedAim,
        onAimChange,
        onClearFilters,
        tags,
        proficiencyLevels,
        aims,
        selectedItems = [],
        onItemSelect,
        onClearSelection,
        onDeleteSelected,
        isItemSelected
    } = props;

    const currentFolderId = currentFolder?._id || null;
    const [showFilters, setShowFilters] = useState(false);

    // Check if any filter is active
    const hasActiveFilters = selectedTag !== '' || selectedProficiencyLevel !== '' || selectedAim !== '';
    const activeFilterCount = [selectedTag, selectedProficiencyLevel, selectedAim].filter(f => f !== '').length;

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ['section', 'task'],
        drop: (item: { id: string; type: 'section' | 'task'; sectionId?: string; taskId?: string; selectedItems?: SelectedItem[] }, monitor) => {
            if (monitor.didDrop()) return;

            // Check if dropping multiple items
            if (item.selectedItems && item.selectedItems.length > 1) {
                item.selectedItems.forEach(si => {
                    onDropItem(si.id, si.type, currentFolderId);
                });
                if (onClearSelection) onClearSelection();
            } else {
                const itemId = item.type === 'section' ? item.sectionId || item.id : item.taskId || item.id;
                if (itemId) {
                    onDropItem(itemId, item.type, currentFolderId);
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [currentFolderId, onDropItem, onClearSelection]);

    const handleFolderDrop = (sectionId: string) => (itemId: string, itemType: 'section' | 'task') => {
        onDropItem(itemId, itemType, sectionId);
    };

    const handleFolderDropMultiple = (sectionId: string) => (items: SelectedItem[]) => {
        items.forEach(item => {
            onDropItem(item.id, item.type, sectionId);
        });
        if (onClearSelection) onClearSelection();
    };

    const handleContentAreaClick = (e: React.MouseEvent) => {
        // Clear selection when clicking on empty area
        if (e.target === e.currentTarget && onClearSelection) {
            onClearSelection();
        }
    };

    const handleClearFilters = () => {
        if (onClearFilters) {
            onClearFilters();
        }
        setShowFilters(false);
    };

    return (
        <div className="folder-content">
            {/* Toolbar */}
            <div className="toolbar">
                <div className="toolbar-left">
                    {/* Show breadcrumb OR filters */}
                    {showFilters || hasActiveFilters ? (
                        <div className="toolbar-filters">
                            <select
                                name="selected_tag"
                                className="filter-select"
                                value={selectedTag}
                                onChange={onTagChange}
                            >
                                <option value="">{t("- Tag -")}</option>
                                {tags.map((tag) => (
                                    <option key={tag._id} value={tag._id}>
                                        {tag.name}
                                    </option>
                                ))}
                            </select>
                            <select
                                name="selected_proficiency_level"
                                className="filter-select"
                                value={selectedProficiencyLevel}
                                onChange={onProficiencyLevelChange}
                            >
                                <option value="">{t("- Level -")}</option>
                                {proficiencyLevels.map((level) => (
                                    <option key={level.value} value={level.value}>
                                        {level.description}
                                    </option>
                                ))}
                            </select>
                            <select
                                name="selected_aim"
                                className="filter-select"
                                value={selectedAim}
                                onChange={onAimChange}
                            >
                                <option value="">{t("- Aim -")}</option>
                                {aims.map((aim) => (
                                    <option key={aim.value} value={aim.value}>
                                        {aim.description}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="toolbar-btn-icon"
                                onClick={handleClearFilters}
                                title={t("Clear filters")}
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    ) : (
                        <div className="breadcrumb-nav">
                            <span
                                className="breadcrumb-item clickable"
                                onClick={() => onNavigateToFolder(null)}
                            >
                                <i className="bi bi-house-door"></i>
                            </span>
                            {folderPath.map((folder, index) => (
                                <React.Fragment key={folder._id}>
                                    <span className="breadcrumb-separator">/</span>
                                    <span
                                        className={`breadcrumb-item ${index === folderPath.length - 1 ? 'current' : 'clickable'}`}
                                        onClick={() => index < folderPath.length - 1 && onNavigateToFolder(folder._id)}
                                    >
                                        {folder.name}
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                <div className="toolbar-right">
                    {/* Filter toggle button (only show when not in filter mode) */}
                    {!showFilters && !hasActiveFilters && (
                        <button
                            className={`toolbar-btn-icon ${hasActiveFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(true)}
                            title={t("Filter")}
                        >
                            <i className="bi bi-funnel"></i>
                        </button>
                    )}
                    <div className="view-toggle">
                        <button
                            className={`view-toggle-btn ${viewMode === 'icons' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('icons')}
                            title={t("Icon view")}
                        >
                            <i className="bi bi-grid-3x3-gap-fill"></i>
                        </button>
                        <button
                            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('list')}
                            title={t("List view")}
                        >
                            <i className="bi bi-list-ul"></i>
                        </button>
                    </div>
                    <div className="toolbar-actions">
                        <button
                            className="toolbar-btn"
                            onClick={onCreateFolder}
                            title={t("Create folder")}
                        >
                            <i className="bi bi-folder-plus"></i>
                            <span>{t("New Folder")}</span>
                        </button>
                        <button
                            className="toolbar-btn primary"
                            onClick={onCreateTask}
                            title={t("Create task")}
                        >
                            <i className="bi bi-file-earmark-plus"></i>
                            <span>{t("New Task")}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content area */}
            <div
                ref={drop as any}
                className={`content-area ${viewMode === 'list' ? 'content-list' : 'content-grid'} ${isOver && canDrop ? 'drop-target' : ''}`}
                onClick={handleContentAreaClick}
            >
                {/* Subfolders first (only show when not filtering) */}
                {!hasActiveFilters && subfolders.sort((a, b) => a.weight - b.weight).map(folder => (
                    <FolderIcon
                        key={folder._id}
                        section={folder}
                        viewMode={viewMode}
                        onDoubleClick={() => onNavigateToFolder(folder._id)}
                        onEdit={() => onEditFolder(folder)}
                        onDelete={() => onDeleteFolder(folder._id)}
                        onDeleteSelected={onDeleteSelected}
                        onDrop={handleFolderDrop(folder._id)}
                        onDropMultiple={handleFolderDropMultiple(folder._id)}
                        onCreateSubfolder={() => onCreateSubfolder(folder._id)}
                        isSelected={isItemSelected ? isItemSelected(folder._id, 'section') : false}
                        onSelect={(ctrlKey) => onItemSelect && onItemSelect(folder._id, 'section', ctrlKey)}
                        selectedItems={selectedItems}
                    />
                ))}

                {/* Tasks */}
                {tasks.sort((a, b) => a.weight - b.weight).map(task => (
                    <TaskIcon
                        key={task._id}
                        task={task}
                        viewMode={viewMode}
                        onDoubleClick={() => onEditTask(task._id)}
                        onDelete={() => onDeleteTask(task._id)}
                        onDeleteSelected={onDeleteSelected}
                        onDuplicate={() => onDuplicateTask(task)}
                        onShare={onShareTask ? () => onShareTask(task) : undefined}
                        onShareLink={onShareLinkTask ? () => onShareLinkTask(task) : undefined}
                        isSelected={isItemSelected ? isItemSelected(task._id, 'task') : false}
                        onSelect={(ctrlKey) => onItemSelect && onItemSelect(task._id, 'task', ctrlKey)}
                        selectedItems={selectedItems}
                    />
                ))}

                {/* Empty state */}
                {subfolders.length === 0 && tasks.length === 0 && !hasActiveFilters && (
                    <div className="empty-folder">
                        <i className="bi bi-folder2-open"></i>
                        <p>{t("This folder is empty")}</p>
                        <p className="hint">{t("Create a new task or folder, or drag items here")}</p>
                    </div>
                )}

                {/* Empty filter results */}
                {tasks.length === 0 && hasActiveFilters && (
                    <div className="empty-folder">
                        <i className="bi bi-search"></i>
                        <p>{t("No tasks match the filters")}</p>
                        <p className="hint">{t("Try adjusting or clearing the filters")}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
